/**
 * Types + fetcher dla listy użytkowników systemu w panelu konta
 * (`c_user_accounts` -> grid "Użytkownicy").
 *
 * Backend endpoint:
 *   GET /IRESTAPI/v1/C_USER_ACCOUNTS_CONTROLLER/userList?id_catalog=N
 * (requiresUser; grupy rozwiązywane server-side z configu katalogu).
 *
 * Frontend route handler proxy: app/api/iiapi/c-user-accounts/user-list/.
 */

export type UserListRow = {
  id: number;
  /** Login odbiorcy - używany przez akcję "Wyślij wiadomość" (compose po loginie). */
  login: string;
  name: string;
  surname: string;
  /** `null` gdy brak / puste. */
  email: string | null;
  /** `null` gdy brak / puste. */
  telephone: string | null;
  /** Pełny URL miniatury sq_ (120x120) lub `null`. */
  avatar_url: string | null;
};

export type UserListResponse = {
  ok: true;
  data: {
    users: UserListRow[];
    /** `true` gdy lista została obcięta do limitu serwera (np. 500). */
    truncated: boolean;
  };
};

/**
 * Pobiera listę użytkowników z route handler proxy.
 *
 * @param idCatalog ID katalogu c_user_accounts (z `content.id_catalog`).
 * @returns `{ users, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsługuje przez try/catch).
 */
export async function fetchUserList(idCatalog: number): Promise<{
  users: UserListRow[];
  truncated: boolean;
}> {
  const res = await fetch(
    `/api/iiapi/c-user-accounts/user-list?id_catalog=${encodeURIComponent(String(idCatalog))}`,
    { cache: "no-store" },
  );
  if (!res.ok) {
    throw new Error(`Fetch user list failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserListResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    users: body.data?.users ?? [],
    truncated: body.data?.truncated ?? false,
  };
}
